import { BuildingOffice2Icon, ClockIcon, FolderIcon } from "@heroicons/react/20/solid";

import CompanyLogo from "~/components/CompanyLogo";
import Tooltip from "~/components/Tooltip";
import type { MonthlyClosingStaffingUnlock } from "~/modules/timeTracking/api/monthlyClosing/monthlyClosingTypes.ts";
import type { ClosingListProps } from "~/modules/timeTracking/components/closingLists/closingListTypes.ts";
import { nonProjectTitle } from "~/modules/timeTracking/components/closingLists/closingListTypes.ts";
import { NonProjectActions, StaffingActions } from "~/modules/timeTracking/components/closingLists/ClosingPartActions.tsx";
import { MonthlyClosingState } from "~/modules/timeTracking/components/components/MonthlyClosingState";
import { formatDateCompact, parseDateString } from "~/utils/dateAndTimeUtils";

function unlockLine(unlock: MonthlyClosingStaffingUnlock): string {
	const start = formatDateCompact(parseDateString(unlock.deliveryStartDate));
	const end = formatDateCompact(parseDateString(unlock.deliveryEndDate));
	return `Entsperrt durch Rechnung ${unlock.invoiceNumber} (${start}–${end})`;
}

/** Variant A: one table. Project rows are gray bands, the non-project row is a separate block below. */
export function ClosingListTable(props: ClosingListProps) {
	return (
		<div className="flex flex-col gap-4 text-xs">
			{props.groups.length > 0 && <div className="overflow-hidden rounded-md border border-gray-200">
				{props.groups.map((group) => (
					<div key={group.projectId}
						 className="border-b border-gray-200 last:border-b-0">
						<div className="flex items-center gap-2 bg-gray-50 px-3 py-1.5 font-semibold text-gray-800">
							{group.companyAvatarImage ?
								<CompanyLogo image={group.companyAvatarImage}
											 sizeClassName="size-5" /> :
								<FolderIcon className="size-4 text-primary-300" />}
							{group.projectName}
						</div>
						<ul>
							{group.staffings.map((staffing) => (
								<li key={staffing.staffingId}
									className="grid grid-cols-[1fr_8rem_8rem] items-center gap-2 border-t border-gray-100 py-1 pl-9 pr-3">
									<span className="truncate text-gray-700">{staffing.orderName}</span>
									{/* The backend sends unlocks only for a closed part: an unlock explains why a closed staffing stays editable. */}
									{staffing.unlocks.length > 0 ? (
										<Tooltip text={staffing.unlocks.map(unlockLine).join("\n")}
												 className="whitespace-pre-line">
											<span className="col-span-2 flex min-w-0 items-center gap-1 whitespace-nowrap text-amber-700">
												<ClockIcon className="size-4 shrink-0" />
												temporär entsperrt, Erfassungen möglich
											</span>
										</Tooltip>
									) : (
										<>
											<MonthlyClosingState isClosed={staffing.isClosed} />
											<div className="flex justify-end">
												<StaffingActions staffing={staffing}
																 projectName={group.projectName}
																 onCloseRequest={props.onCloseRequest}
																 onReopenRequest={props.onReopenRequest} />
											</div>
										</>
									)}
								</li>
							))}
						</ul>
					</div>
				))}
			</div>}
			<div className="grid grid-cols-[1fr_8rem_8rem] items-center gap-2 rounded-md border border-dashed border-accent-500 bg-accent-500/5 px-3 py-2">
				<span className="flex items-center gap-2 font-semibold text-gray-800">
					<BuildingOffice2Icon className="size-4 text-accent-500" />
					{nonProjectTitle}
				</span>
				<MonthlyClosingState isClosed={props.nonProject.isClosed} />
				<div className="flex justify-end">
					<NonProjectActions nonProject={props.nonProject}
									   onCloseRequest={props.onCloseRequest}
									   onReopenRequest={props.onReopenRequest} />
				</div>
			</div>
		</div>
	);
}
